/*
* <license header>
*/

import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Grid,
  Heading,
  Item,
  minmax,
  Picker,
  repeat,
  Text,
  TextField,
  View
} from '@adobe/react-spectrum'
import Play from '@spectrum-icons/workflow/Play'
import PlayCircle from '@spectrum-icons/workflow/PlayCircle'
import Stop from '@spectrum-icons/workflow/Stop'
import StopCircle from '@spectrum-icons/workflow/StopCircle'
import UploadToCloud from '@spectrum-icons/workflow/UploadToCloud'
import Delete from '@spectrum-icons/workflow/Delete'
import ChevronLeft from '@spectrum-icons/workflow/ChevronLeft'
import ChevronRight from '@spectrum-icons/workflow/ChevronRight'
import { PageHeaderBanner } from '../shared/PageHeaderBanner'
import { AppAssistant } from '../shared/AppAssistant'
import { useNavigate, useParams } from 'react-router-dom'
import { APPS, APP_TYPES } from '../../data/constants'

const previewImage = new URL('../../assets/preview.png', import.meta.url).href

const getMockAppInfo = (appId) => {
  const app = APPS.find((a) => a.id === appId)
  const appTypeByApp = {
    '1': APP_TYPES.find((t) => t.slug === 'generator-app-asset-compute'),
    '2': APP_TYPES.find((t) => t.slug === 'workfront-ui-ext-tpl'),
    '3': APP_TYPES.find((t) => t.slug === 'generator-app-api-mesh')
  }
  const projectIdByApp = { '1': '27200', '2': '27201', '3': '27202' }
  const slugByApp = { '1': 'appbuilderfactory', '2': 'appbuilderfactory', '3': 'appbuilderfactory' }
  const repoByApp = { '1': 'adobe/appbuilderfactory', '2': 'adobe/appbuilderfactory-genstudio', '3': 'adobe/appbuilderfactory-api' }
  const projectNameByApp = { '1': 'AppBuilder Factory', '2': 'AppBuilder Factory GenStudio', '3': 'AppBuilder Factory API' }
  const projectId = projectIdByApp[appId] || '27200'
  const slug = slugByApp[appId] || 'appbuilderfactory'
  const stagingUrl = `https://${projectId}-${slug}-stage.adobeio-static.net`
  const productionUrl = `https://${projectId}-${slug}.adobeio-static.net`
  return {
    appName: app?.title ?? `App ${appId}`,
    adobeIoProjectName: projectNameByApp[appId] ?? 'AppBuilder Factory',
    repository: repoByApp[appId] ?? 'adobe/appbuilderfactory',
    applicationType: appTypeByApp[appId]?.title ?? 'Unified Shell Application',
    workspaceUrls: { production: productionUrl, staging: stagingUrl }
  }
}

const WORKSPACES = [
  { id: 'production', name: 'Production' },
  { id: 'staging', name: 'Staging' }
]

const PREVIEW_SECTION_MIN_HEIGHT = 600

const MOCK_INBOUND_PARAMS = [
  { name: 'objectId', type: 'string', description: 'ID of the current Workfront object' },
  { name: 'objectType', type: 'string', description: 'Type of object (project, task, issue)' },
  { name: 'projectId', type: 'string', description: 'Current project context' },
  { name: 'userSession', type: 'object', description: 'Authenticated user session data' }
]

const MOCK_OUTBOUND_PARAMS = [
  { name: 'closeModal', type: 'function', description: 'Closes the extension modal' },
  { name: 'navigateTo', type: 'function', description: 'Navigates to a Workfront object' },
  { name: 'refreshContext', type: 'function', description: 'Refreshes the host context' }
]

const WORKSPACE_STATUS_CONFIG = {
  idle: { label: 'Not deployed', color: '#9e9e9e' },
  deploying: { label: 'Deploying', color: '#2196f3' },
  running: { label: 'Running', color: '#4caf50' }
}

const formatDeploymentDate = (date) => {
  if (!date) return '—'
  const d = new Date(date)
  const dateStr = d.toLocaleDateString()
  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return `${dateStr} ${timeStr}`
}

export const AppBrowserDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const appInfo = getMockAppInfo(id)
  const [vibeCodingRunning, setVibeCodingRunning] = useState(false)
  const [interstitialMessage, setInterstitialMessage] = useState(null)
  const [selectedWorkspace, setSelectedWorkspace] = useState('production')
  const [workspaceData, setWorkspaceData] = useState({
    production: { status: 'idle', deploymentDate: null },
    staging: { status: 'idle', deploymentDate: null }
  })
  const [inboundParams, setInboundParams] = useState(
    Object.fromEntries(MOCK_INBOUND_PARAMS.map((p) => [p.name, '']))
  )
  const [outboundParams, setOutboundParams] = useState(
    Object.fromEntries(MOCK_OUTBOUND_PARAMS.map((p) => [p.name, '']))
  )
  const [paramsPanelOpen, setParamsPanelOpen] = useState(false)
  const [assistantPanelOpen, setAssistantPanelOpen] = useState(false)
  const [previewZoom, setPreviewZoom] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const previewScrollRef = useRef(null)
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 })

  const handlePreviewMouseDown = (e) => {
    if (e.button !== 0) return
    const el = previewScrollRef.current
    if (!el) return
    setIsDragging(true)
    dragStartRef.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop }
  }

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e) => {
      const el = previewScrollRef.current
      if (!el) return
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      el.scrollLeft = dragStartRef.current.scrollLeft - dx
      el.scrollTop = dragStartRef.current.scrollTop - dy
    }
    const onUp = () => setIsDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [isDragging])

  useEffect(() => {
    if (!interstitialMessage) return
    const timer = setTimeout(() => {
      if (interstitialMessage === 'starting up') {
        setVibeCodingRunning(true)
      } else if (interstitialMessage === 'shutting down') {
        setVibeCodingRunning(false)
      }
      setInterstitialMessage(null)
    }, 3000)
    return () => clearTimeout(timer)
  }, [interstitialMessage])

  const handleStart = () => {
    setInterstitialMessage('starting up')
    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById('app-preview-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    })
  }
  const handleShutdown = () => setInterstitialMessage('shutting down')

  const handleDeleteConfirm = () => {
    console.log('Delete app', id)
    navigate('/apps')
  }

  const handleDeploy = () => {
    setWorkspaceData((prev) => ({
      ...prev,
      [selectedWorkspace]: { ...prev[selectedWorkspace], status: 'deploying' }
    }))
    setTimeout(() => {
      setWorkspaceData((prev) => ({
        ...prev,
        [selectedWorkspace]: {
          status: 'running',
          deploymentDate: new Date().toISOString()
        }
      }))
    }, 2000)
  }

  return (
    <View width="100%" padding="size-400" UNSAFE_style={{ paddingBottom: '2.5rem' }}>
      <PageHeaderBanner
        title="AppBuilder Factory"
        subtitle="AppBuilder Factory App Repository"
      />

      <View
        UNSAFE_style={{
          border: '1px solid var(--card-border)',
          borderRadius: '8px',
          padding: '1.25rem',
          backgroundColor: 'var(--white)'
        }}
        marginBottom="size-300"
      >
        <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '0.75rem', fontSize: '1.125rem' }}>
          App Info
        </Heading>
        <View UNSAFE_style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>App name</td>
                <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{appInfo.appName}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>Adobe I/O Project name</td>
                <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{appInfo.adobeIoProjectName}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>Repository</td>
                <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{appInfo.repository}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 1rem 0.5rem 0', fontWeight: 600, color: 'var(--gray-900)', verticalAlign: 'top' }}>Application type</td>
                <td style={{ padding: '0.5rem 0', color: 'var(--gray-700)' }}>{appInfo.applicationType}</td>
              </tr>
            </tbody>
          </table>
        </View>
      </View>

      <View marginBottom="size-300">
        <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '1rem', fontSize: '1.125rem' }}>
          Workspaces
        </Heading>
        <Grid
          columns={repeat('auto-fill', minmax('size-3000', '1fr'))}
          autoRows="auto"
          gap="size-200"
          width="100%"
        >
          {WORKSPACES.map((ws) => {
            const data = workspaceData[ws.id]
            const statusConfig = WORKSPACE_STATUS_CONFIG[data?.status || 'idle']
            return (
              <View
                key={ws.id}
                UNSAFE_style={{
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  backgroundColor: 'var(--white)',
                  minHeight: '10rem'
                }}
              >
                <View
                  UNSAFE_style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: statusConfig.color,
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    marginBottom: '0.75rem'
                  }}
                >
                  {statusConfig.label}
                </View>
                <Heading level={3} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.125rem' }}>
                  {ws.name}
                </Heading>
                <Flex direction="column" gap="size-100">
                  <Text UNSAFE_style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    <strong>Deployment date:</strong> {formatDeploymentDate(data?.deploymentDate)}
                  </Text>
                  <Text UNSAFE_style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    <strong>Status:</strong> {statusConfig.label}
                  </Text>
                  <Text UNSAFE_style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    <strong>URL:</strong>{' '}
                    <a href={appInfo.workspaceUrls?.[ws.id] ?? appInfo.workspaceUrls?.staging} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--spectrum-global-color-blue-600)', textDecoration: 'underline' }}>
                      {appInfo.workspaceUrls?.[ws.id] ?? appInfo.workspaceUrls?.staging}
                    </a>
                  </Text>
                </Flex>
              </View>
            )
          })}
        </Grid>
      </View>

      <View
        UNSAFE_style={{
          border: '1px solid var(--card-border)',
          borderRadius: '8px',
          padding: '1.25rem',
          backgroundColor: 'var(--white)'
        }}
        marginBottom="size-300"
      >
        <Heading level={2} UNSAFE_style={{ margin: 0, marginBottom: '1rem', fontSize: '1.125rem' }}>
          App Actions
        </Heading>
        <Flex direction="row" gap="size-200" wrap>
          <View
            flex={1}
            UNSAFE_style={{
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: 'var(--white)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: '12rem'
            }}
          >
            <Heading level={3} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '0.9375rem', textAlign: 'center', width: '100%' }}>
              Vibe Coding Instance
            </Heading>
            <Flex flex={1} gap="size-100" justifyContent="center" alignItems="center" wrap>
              <Button
                variant="accent"
                onPress={handleStart}
                isDisabled={!!interstitialMessage}
                UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem' }}
              >
                <Play size="S" />
                <span style={{ marginLeft: '0.5rem' }}>Start</span>
              </Button>
              <Button
                variant="secondary"
                onPress={handleShutdown}
                isDisabled={!!interstitialMessage}
                UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem' }}
              >
                <Stop size="S" />
                <span style={{ marginLeft: '0.5rem' }}>Shutdown</span>
              </Button>
            </Flex>
          </View>

          <View
            flex={1}
            UNSAFE_style={{
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: 'var(--white)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: '12rem'
            }}
          >
            <Heading level={3} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '0.9375rem', textAlign: 'center', width: '100%' }}>
              Deploy
            </Heading>
            <Flex flex={1} gap="size-100" direction="column" alignItems="stretch" justifyContent="center" UNSAFE_style={{ width: '100%' }}>
              <Picker
                label="Workspaces"
                aria-label="Workspaces"
                selectedKey={selectedWorkspace}
                onSelectionChange={setSelectedWorkspace}
                items={WORKSPACES}
                width="100%"
              >
                {(item) => <Item key={item.id}>{item.name}</Item>}
              </Picker>
              <Button
                variant="accent"
                onPress={handleDeploy}
                isDisabled={workspaceData[selectedWorkspace]?.status === 'deploying'}
                UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem', alignSelf: 'center' }}
              >
                <UploadToCloud size="S" />
                <span style={{ marginLeft: '0.5rem' }}>Deploy</span>
              </Button>
            </Flex>
          </View>

          <View
            flex={1}
            UNSAFE_style={{
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: 'var(--white)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: '12rem'
            }}
          >
            <Heading level={3} UNSAFE_style={{ margin: 0, marginBottom: '0.5rem', fontSize: '0.9375rem', textAlign: 'center', width: '100%' }}>
              Delete App
            </Heading>
            <Flex flex={1} justifyContent="center" alignItems="center">
            <DialogTrigger>
              <Button
                variant="negative"
                UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem' }}
              >
                <Delete size="S" />
                <span style={{ marginLeft: '0.5rem' }}>Delete app</span>
              </Button>
              {(close) => (
                <Dialog>
                  <Heading>Delete app</Heading>
                  <Divider />
                  <Content>
                    <Text>
                      This app will be disconnected from AppBuilder Factory. To re-enable management, the app will need to be added again to the factory.
                    </Text>
                  </Content>
                  <ButtonGroup>
                    <Button variant="secondary" onPress={close} UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                      Cancel
                    </Button>
                    <Button variant="negative" onPress={() => { handleDeleteConfirm(); close() }} UNSAFE_style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                      Confirm
                    </Button>
                  </ButtonGroup>
                </Dialog>
              )}
            </DialogTrigger>
            </Flex>
          </View>
        </Flex>
      </View>

      {(vibeCodingRunning || interstitialMessage) && (
      <>
      <Heading level={2} UNSAFE_style={{ margin: 0, marginTop: '0.75rem', marginBottom: '1rem', fontSize: '1.125rem' }}>
        AppBuilder App Preview
      </Heading>
      <div id="app-preview-section">
      <Flex
        direction="row"
        gap="size-300"
        marginTop="size-300"
        UNSAFE_style={{ minHeight: PREVIEW_SECTION_MIN_HEIGHT }}
      >
        {interstitialMessage ? (
          <View
            flex={1}
            UNSAFE_style={{
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              padding: '1.25rem',
              backgroundColor: 'var(--white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              minHeight: PREVIEW_SECTION_MIN_HEIGHT
            }}
          >
            <Flex direction="column" alignItems="center" gap="size-200">
              {interstitialMessage === 'starting up' ? (
                <PlayCircle size="XL" UNSAFE_style={{ color: 'var(--card-accent)' }} />
              ) : (
                <StopCircle size="XL" UNSAFE_style={{ color: 'var(--gray-600)' }} />
              )}
              <Text UNSAFE_style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                {interstitialMessage === 'starting up' ? 'Starting up' : 'Shutting down'}
              </Text>
            </Flex>
          </View>
        ) : (
          <View
            flex={1}
            UNSAFE_style={{
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative',
              minHeight: PREVIEW_SECTION_MIN_HEIGHT,
              height: PREVIEW_SECTION_MIN_HEIGHT
            }}
          >
            {/* Preview image as background */}
            <div
              ref={previewScrollRef}
              role="img"
              aria-label="App preview"
              onMouseDown={handlePreviewMouseDown}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              style={{
                position: 'absolute',
                inset: 0,
                overflow: 'auto',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
              }}
            >
              <div
                style={{
                  width: `${100 * previewZoom}%`,
                  height: `${100 * previewZoom}%`,
                  minWidth: '100%',
                  minHeight: '100%'
                }}
              >
                <img
                  src={previewImage}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            </div>

            {/* Zoom controls: bottom left */}
            <div
              style={{
                position: 'absolute',
                bottom: '1rem',
                left: '1rem',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                background: 'rgba(255,255,255,0.95)',
                border: '1px solid var(--card-border)',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPreviewZoom((z) => Math.max(0.5, z - 0.25)) }}
                aria-label="Zoom out"
                style={{
                  width: '1.75rem',
                  height: '1.75rem',
                  padding: 0,
                  border: '1px solid var(--card-border)',
                  borderRadius: '4px',
                  background: 'var(--white)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                −
              </button>
              <span style={{ fontSize: '0.75rem', minWidth: '2.5rem', textAlign: 'center', fontWeight: 500 }}>
                {Math.round(previewZoom * 100)}%
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPreviewZoom((z) => Math.min(2, z + 0.25)) }}
                aria-label="Zoom in"
                style={{
                  width: '1.75rem',
                  height: '1.75rem',
                  padding: 0,
                  border: '1px solid var(--card-border)',
                  borderRadius: '4px',
                  background: 'var(--white)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                +
              </button>
            </div>

            {/* Parameters: floating panel (not anchored to left edge) */}
            <View
              UNSAFE_style={{
                position: 'absolute',
                top: '1rem',
                left: '1rem',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start'
              }}
            >
              <button
                type="button"
                onClick={() => setParamsPanelOpen((v) => !v)}
                aria-label={paramsPanelOpen ? 'Collapse parameters' : 'Expand parameters'}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  background: 'var(--white)',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                {paramsPanelOpen ? <ChevronLeft size="S" /> : <ChevronRight size="S" />}
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>Parameters</span>
              </button>
              {paramsPanelOpen && (
                <View
                  UNSAFE_style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    background: 'var(--white)',
                    border: '1px solid var(--card-border)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    minWidth: '12rem',
                    maxWidth: '20rem',
                    maxHeight: '32rem',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ fontSize: '0.6875rem' }}>
                  <Flex direction="row" gap="size-0" wrap>
                    <Flex direction="row" UNSAFE_style={{ flex: 1, minWidth: '5rem' }}>
                      <View UNSAFE_style={{ display: 'flex', alignItems: 'center', width: '1rem', flexShrink: 0 }}>
                        <Text UNSAFE_style={{ fontSize: '0.5625rem', fontWeight: 700, color: 'var(--gray-700)', transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>IN</Text>
                      </View>
                      <View UNSAFE_style={{ flex: 1, padding: '0.2rem 0.35rem', borderRadius: '4px', backgroundColor: 'var(--gray-75)' }}>
                        <Flex direction="column" gap="size-50">
                          {MOCK_INBOUND_PARAMS.map((p) => (
                            <TextField key={p.name} label={p.name} value={inboundParams[p.name] ?? ''} onChange={(v) => setInboundParams((prev) => ({ ...prev, [p.name]: v }))} width="100%" />
                          ))}
                        </Flex>
                      </View>
                    </Flex>
                    <Flex direction="row" UNSAFE_style={{ flex: 1, minWidth: '5rem', borderLeft: '1px solid var(--card-border)', marginLeft: '0.35rem', paddingLeft: '0.35rem' }}>
                      <View UNSAFE_style={{ display: 'flex', alignItems: 'center', width: '1rem', flexShrink: 0 }}>
                        <Text UNSAFE_style={{ fontSize: '0.5625rem', fontWeight: 700, color: 'var(--gray-700)', transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>OUT</Text>
                      </View>
                      <View UNSAFE_style={{ flex: 1, padding: '0.2rem 0.35rem', borderRadius: '4px', backgroundColor: 'var(--gray-75)' }}>
                        <Flex direction="column" gap="size-50">
                          {MOCK_OUTBOUND_PARAMS.map((p) => (
                            <TextField key={p.name} label={p.name} value={outboundParams[p.name] ?? ''} onChange={(v) => setOutboundParams((prev) => ({ ...prev, [p.name]: v }))} width="100%" />
                          ))}
                        </Flex>
                      </View>
                    </Flex>
                  </Flex>
                  </div>
                </View>
              )}
            </View>

            {/* Assistant: floating panel (top right, same style as Parameters) */}
            <View
              UNSAFE_style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'flex-end'
              }}
            >
              {assistantPanelOpen && (
                <View
                  UNSAFE_style={{
                    marginRight: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    background: 'var(--white)',
                    border: '1px solid var(--card-border)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    width: '260px',
                    maxHeight: '32rem',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <View UNSAFE_style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--card-border)', fontWeight: 600, fontSize: '0.75rem' }}>
                    APP Assistant
                  </View>
                  <View UNSAFE_style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <AppAssistant embedded />
                  </View>
                </View>
              )}
              <button
                type="button"
                onClick={() => setAssistantPanelOpen((v) => !v)}
                aria-label={assistantPanelOpen ? 'Collapse assistant' : 'Expand assistant'}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  background: 'var(--white)',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                {assistantPanelOpen ? <ChevronRight size="S" /> : <ChevronLeft size="S" />}
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>App Assistant</span>
              </button>
            </View>
          </View>
        )}
      </Flex>
      </div>
      </>
      )}
    </View>
  )
}
